import { useEffect, useState } from "react";
import axios from "axios";
import { Plus } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FEATURE_IMG =
  "https://customer-assets.emergentagent.com/job_bespoke-events-3/artifacts/7r9uugyf_image.png";

const Services = () => {
  const [services, setServices] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/services`)
      .then((res) => setServices(res.data.services || []))
      .catch(() => setServices([]));
  }, []);

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-24 md:py-40 px-6 md:px-12 lg:px-24 bg-[#0B0F18]"
    >
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-12 gap-10 mb-16 md:mb-20">
          <div className="lg:col-span-5 reveal">
            <div className="overline mb-6">What We Do</div>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-light leading-[1.05] tracking-tight">
              A full house of
              <br />
              <span className="italic text-[#B8935C]">wedding</span> craft.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 lg:pt-6 reveal">
            <p className="text-[#F0EBE1]/70 text-base sm:text-lg leading-relaxed font-light max-w-lg">
              Ten dedicated disciplines, one orchestrated celebration. We move
              between strategist, designer, diplomat and dreamer — so that your
              wedding feels effortless and entirely yours.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-12 gap-10 lg:gap-16">
          {/* Featured image — sticky on desktop */}
          <div className="lg:col-span-5 reveal">
            <div className="lg:sticky lg:top-28">
              <div className="relative overflow-hidden">
                <img
                  src={FEATURE_IMG}
                  alt="A Bon Evento bride at the palace doorway"
                  className="w-full h-[480px] md:h-[640px] object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-[#080B12]/85 via-[#080B12]/20 to-transparent" />
                <div className="absolute inset-0 ring-1 ring-inset ring-[#F0EBE1]/10" />

                <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                  <div className="overline mb-3 text-[10px]">The Bon Evento Way</div>
                  <p className="font-serif text-2xl md:text-3xl text-[#F0EBE1] font-light italic leading-tight max-w-sm">
                    "Every wedding is a private cinema — and the couple, the only stars."
                  </p>
                </div>
              </div>

              <div className="hidden lg:flex items-center gap-4 mt-8">
                <div className="h-px w-12 bg-[#B8935C]" />
                <span className="overline text-[10px]">10 disciplines · one studio</span>
              </div>
            </div>
          </div>

          {/* Services list */}
          <div className="lg:col-span-7">
            <div className="border-t border-[#F0EBE1]/10">
              {services.map((s, idx) => (
                <button
                  key={s.slug}
                  data-testid={`service-row-${s.slug}`}
                  onClick={() => setActive(s)}
                  className="group w-full grid grid-cols-12 gap-4 items-center text-left py-6 md:py-7 border-b border-[#F0EBE1]/10 hover:bg-[#121623]/60 transition-colors px-2 reveal"
                >
                  <div className="col-span-2 md:col-span-1 font-serif text-xl md:text-2xl text-[#B8935C]/60 group-hover:text-[#B8935C] transition-colors">
                    {String(idx + 1).padStart(2, "0")}
                  </div>
                  <div className="col-span-7 md:col-span-8">
                    <h3 className="font-serif text-xl md:text-2xl text-[#F0EBE1] group-hover:text-[#B8935C] transition-colors leading-tight">
                      {s.title}
                    </h3>
                    <p className="hidden md:block mt-1 text-[#F0EBE1]/55 text-sm font-light leading-relaxed">
                      {s.summary}
                    </p>
                  </div>
                  <div className="col-span-3 md:col-span-3 justify-self-end">
                    <span className="inline-flex items-center justify-center w-10 h-10 border border-[#F0EBE1]/15 text-[#F0EBE1]/55 group-hover:border-[#B8935C] group-hover:text-[#B8935C] group-hover:rotate-90 transition-all duration-500">
                      <Plus size={14} />
                    </span>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      <Dialog open={!!active} onOpenChange={() => setActive(null)}>
        <DialogContent
          data-testid="service-dialog"
          className="bg-[#121623] border border-[#F0EBE1]/10 text-[#F0EBE1] max-w-2xl"
        >
          {active && (
            <>
              <DialogHeader>
                <div className="overline mb-3">Service</div>
                <DialogTitle className="font-serif text-3xl md:text-4xl font-light text-[#F0EBE1]">
                  {active.title}
                </DialogTitle>
                <DialogDescription className="text-[#F0EBE1]/65 text-base font-light leading-relaxed pt-3">
                  {active.details}
                </DialogDescription>
              </DialogHeader>
              <div className="pt-4 border-t border-[#F0EBE1]/10 mt-3">
                <a
                  href="#contact"
                  onClick={() => setActive(null)}
                  data-testid="service-dialog-cta"
                  className="inline-flex items-center px-6 py-3 bg-[#B8935C] text-[#080B12] text-xs uppercase tracking-[0.28em] hover:bg-[#D4AF37] transition-all"
                >
                  Enquire about {active.title}
                </a>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </section>
  );
};

export default Services;
